import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { labelFor, SETTING_OPTIONS, estimateMinutes, stepNumberFor } from "../constants";
import { startHunt } from "../../../../utils/huntFirebase";

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
function relLuminance([r, g, b]) {
  const [rl, gl, bl] = [r, g, b].map((c) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}
function contrastRatio(hex1, hex2) {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  if (!c1 || !c2) return null;
  const L1 = relLuminance(c1);
  const L2 = relLuminance(c2);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

function Publish({ hunt, updateHunt, persistHunt, adminToken, organizationId, sessionId, currentTheme, goToStep, onExit }) {
  const [publishing, setPublishing] = useState(false);

  const rc = hunt.roundContent || [];
  const approvedCount = rc.filter((r) => r.approved).length;
  const allApproved = rc.length > 0 && approvedCount === rc.length;
  const checkpoints = hunt.checkpoints || [];
  const short = checkpoints.length < hunt.numTeams;
  const contrast = contrastRatio(hunt.buttonBgColor, hunt.buttonTextColor);
  const passesContrast = contrast != null && contrast >= 4.5;
  const [lo, hi] = estimateMinutes(hunt.numRounds, hunt.setting);

  const checks = useMemo(
    () => [
      {
        id: "rounds",
        label: rc.length ? `${approvedCount} of ${rc.length} rounds approved` : "No rounds set up yet",
        detail: allApproved ? "Content reviewed and locked" : "Approve every round before publishing",
        ok: allApproved,
        step: stepNumberFor("review"),
      },
      {
        id: "identity",
        label: "Client identity set",
        detail: hunt.huntName.trim() && hunt.client.trim() ? `${hunt.huntName} — ${hunt.client}` : "Add a hunt name and client",
        ok: !!hunt.huntName.trim() && !!hunt.client.trim(),
        step: stepNumberFor("basics"),
      },
      {
        id: "contrast",
        label: "Button colours pass contrast",
        detail: contrast != null ? `${contrast.toFixed(1)}:1 between background and text` : "Set button colours",
        ok: passesContrast,
        step: stepNumberFor("basics"),
      },
      {
        id: "collisions",
        label: `${hunt.numTeams} teams, ${checkpoints.length} checkpoints`,
        detail: short ? "Some teams will share a checkpoint each round" : "Every team gets its own checkpoint",
        ok: !short,
        step: stepNumberFor("locations"),
      },
      {
        id: "lobby",
        label: "Lobby code ready",
        detail: hunt.lobbyCode ? `Code ${hunt.lobbyCode}` : "Open Teams to generate one",
        ok: !!hunt.lobbyCode,
        step: stepNumberFor("teams"),
      },
    ],
    [rc.length, approvedCount, allApproved, hunt.huntName, hunt.client, hunt.numTeams, hunt.lobbyCode, contrast, passesContrast, checkpoints.length, short]
  );

  const publish = async () => {
    if (hunt.published) return; // already live — see the disabled "Published" button below
    if (!hunt.huntName.trim()) {
      Swal.fire("Missing hunt name", "Go back to Game basics and name this hunt.", "warning");
      return;
    }
    if (!allApproved) {
      Swal.fire("Content not approved", "Go back to Review content and approve every round.", "warning");
      return;
    }
    setPublishing(true);
    try {
      const nextHunt = { ...hunt, published: true, publishedAt: new Date().toISOString() };
      updateHunt(nextHunt);
      await persistHunt(nextHunt);

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/selectTheme`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ themeName: currentTheme, organizationId, sessionId }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.success === false) {
        throw new Error(json.message || "Failed to activate hunt");
      }

      // Publishing goes live immediately — flip the same Realtime Database flag Live Host's
      // "Start hunt" button uses, so every team waiting in the Waiting Room moves to /game
      // the instant this succeeds, with no separate "start" step required. Raced against a
      // timeout so a stuck/unreachable Firebase connection can never hang "Publishing..."
      // forever — the theme is already live either way (selectTheme above already succeeded).
      try {
        await Promise.race([
          startHunt(organizationId, sessionId),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Firebase timed out")), 5000)),
        ]);
      } catch (e) {
        console.error("[Publish] failed to flip hunt-started flag:", e?.message || e);
      }

      await Swal.fire("Published", `${hunt.huntName} is live.`, "success");
      onExit();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to publish. Please try again.", "error");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="studio-step-page" data-tour="studio-step-publish">
      <h2 className="studio-h2">Publish</h2>
      <p className="studio-sub">Last check before the link goes live. Anything unresolved is listed below.</p>

      <div className="studio-publish-layout">
        <div className="studio-side-card studio-preflight-card">
          <div className="studio-side-eyebrow">Pre-flight</div>
          <ul className="studio-preflight-list">
            {checks.map((c) => (
              <li key={c.id} className="studio-preflight-row">
                <span className={"studio-preflight-icon" + (c.ok ? " studio-preflight-icon--ok" : " studio-preflight-icon--warn")}>
                  <i className={c.ok ? "fa-solid fa-check" : "fa-solid fa-exclamation"} />
                </span>
                <div className="studio-preflight-text">
                  <div className="studio-preflight-label">{c.label}</div>
                  <div className="studio-preflight-detail">{c.detail}</div>
                </div>
                {!c.ok && (
                  <button className="studio-link-btn" onClick={() => goToStep(c.step)}>
                    Review
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="studio-side-card">
          <div className="studio-side-eyebrow">Summary</div>
          <dl className="studio-summary-kv">
            <dt>Hunt</dt>
            <dd>{hunt.huntName || "Untitled hunt"}</dd>
            <dt>Client</dt>
            <dd>{hunt.client || "—"}</dd>
            <dt>Setting</dt>
            <dd>{labelFor(SETTING_OPTIONS, hunt.setting)}</dd>
            <dt>Rounds</dt>
            <dd>{hunt.numRounds}</dd>
            <dt>Teams</dt>
            <dd>{hunt.numTeams}</dd>
            <dt>Est. duration</dt>
            <dd>{lo}–{hi} min</dd>
          </dl>
        </div>
      </div>

      <div className="studio-publish-actions">
        <button
          className={"studio-btn studio-btn--primary studio-btn--publish" + (hunt.published ? " studio-btn--published" : "")}
          onClick={publish}
          disabled={publishing || hunt.published}
        >
          {publishing ? "Publishing..." : hunt.published ? "Published — hunt is live" : "Publish hunt"}
        </button>
      </div>
    </div>
  );
}

export default Publish;
